import { inject, Injectable } from '@angular/core';
import { IContFact } from 'src/app/@interfaces/contfac.interface';
import { IQuota } from 'src/app/@interfaces/quotation.interface';
import { IVentaQuery } from 'src/app/@interfaces/venta.interface';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { GenerateContentPdfService } from 'src/app/@shared/components/services/files/generate-content-pdf.service';
import {
  PdfConfiguration,
  PdfContent,
} from 'src/app/@shared/interfaces/pdf.interface';
import { PdfGeneratorService } from 'src/app/@shared/components/pdf-generator/pdf-generator.service';
import {
  InvoiceStatus,
  InvoiceStatusDescription,
} from 'src/app/@enums/pdf/invoice-status.enum';
import { InvoicePdf } from '@shared-models';
import { PdfElectronicInfoService } from '@shared-services';
(<any>pdfMake).addVirtualFileSystem(pdfFonts);

@Injectable({
  providedIn: 'root',
})
export class GenerateLetterheadPdfService {
  private pdfGeneratorService = inject(PdfGeneratorService);
  private generateContentPdfService = inject(GenerateContentPdfService);
  private pdfElectronicInfoService = inject(PdfElectronicInfoService);

  createInvoiceLetterheadPdf(object: InvoicePdf) {
    const invoiceDate = this.pdfElectronicInfoService.getPdfInvoiceDate(
      object.invoiceObject,
    );
    const items = this.generateInvoicePdfItems(
      object.sales,
      object.rateBCV!,
      object.invoiceObject,
    );
    const content = this.createLetterheadContent(object, items, invoiceDate);
    const styles = this.getStyles();
    const pdfConfiguration: PdfConfiguration = {
      openPDF: true,
      downloadPDF: false,
      content: content,
      styles: styles,
      pageMargins: [40, 130, 40, 40],
      pageSize: 'B4',
    };
    return this.pdfGeneratorService.createPdf(pdfConfiguration);
  }

  createLetterheadContent(
    object: InvoicePdf,
    items: any[],
    invoiceDate: string,
  ) {
    const layoutConfiguration =
      this.generateContentPdfService.createLayout('grey');
    const content: PdfContent[] = [
      {
        style: 'tableData',
        color: '#444',
        table: {
          widths: ['auto', 'auto', '*', 'auto', 'auto'],
          headerRows: 2,
          body: [
            [
              this.generateContentPdfService.createAlignmentObject(
                'Nombre',
                'left',
                'header',
              ),
              this.generateContentPdfService.createAlignmentObject(
                `${object.invoiceObject.cliente.nombre}`,
                'right',
              ),
              this.generateContentPdfService.createAlignmentObject(
                '',
                'center',
              ),
              this.generateContentPdfService.createAlignmentObject(
                'N°. Control',
                'left',
                'header',
              ),
              this.generateContentPdfService.createAlignmentObject('', 'right'),
            ],
            [
              this.generateContentPdfService.createAlignmentObject(
                'Cod. Cliente',
                'left',
                'header',
              ),
              this.generateContentPdfService.createAlignmentObject(
                `${object.invoiceObject.cliente.doc_nit}`,
                'right',
              ),
              this.generateContentPdfService.createAlignmentObject(
                '',
                'center',
              ),
              this.generateContentPdfService.createAlignmentObject(
                this.getPdfInvoiceStatus(object.invoiceObject),
                'left',
                'header',
              ),
              this.generateContentPdfService.createAlignmentObject(
                this.getPdfInvoiceNumber(
                  object.invoiceCount,
                  object.invoiceObject,
                ),
                'right',
              ),
            ],
            [
              this.generateContentPdfService.createAlignmentObject(
                'Direccion',
                'left',
                'header',
              ),
              this.generateContentPdfService.createAlignmentObject(
                `${object.invoiceObject.cliente!.direccion}`,
                'right',
              ),
              this.generateContentPdfService.createAlignmentObject(
                '',
                'center',
              ),
              this.generateContentPdfService.createAlignmentObject(
                'CUA',
                'left',
                'header',
              ),
              this.generateContentPdfService.createAlignmentObject(
                `${invoiceDate}`,
                'right',
              ),
            ],
            [
              this.generateContentPdfService.createAlignmentObject(
                'N° RIF',
                'left',
                'header',
              ),
              this.generateContentPdfService.createAlignmentObject(
                `${object.invoiceObject.cliente!.email}`,
                'right',
              ),
              this.generateContentPdfService.createAlignmentObject(
                '',
                'center',
              ),
              this.generateContentPdfService.createAlignmentObject(
                'Telf',
                'left',
                'header',
              ),
              this.generateContentPdfService.createAlignmentObject(
                `${object.invoiceObject.cliente!.telefono}`,
                'right',
              ),
            ],
            [
              this.generateContentPdfService.createAlignmentObject(
                'Ciudad',
                'left',
                'header',
              ),
              this.generateContentPdfService.createAlignmentObject('', 'right'),
              this.generateContentPdfService.createAlignmentObject(
                '',
                'center',
              ),
              this.generateContentPdfService.createAlignmentObject(
                'Condiciones de Pago',
                'left',
                'header',
              ),
              this.generateContentPdfService.createAlignmentObject('', 'right'),
            ],
            [
              this.generateContentPdfService.createAlignmentObject(
                '',
                'left',
                'header',
              ),
              this.generateContentPdfService.createAlignmentObject('', 'right'),
              this.generateContentPdfService.createAlignmentObject(
                '',
                'center',
              ),
              this.generateContentPdfService.createAlignmentObject(
                'Vendedor',
                'left',
                'header',
              ),
              this.generateContentPdfService.createAlignmentObject(
                `${object.invoiceObject.seller!.nombre}`,
                'right',
              ),
            ],
          ],
        },
      },
      {
        style: 'itemsTable',
        table: {
          widths: ['auto', '*', 'auto', 'auto', 'auto', 'auto', 'auto'],
          body: items,
        },
        layout: layoutConfiguration,
      },
      {
        style: 'tableData',
        color: '#444',
        table: {
          widths: ['*'],
          body: [
            [
              {
                style: 'header',
                border: [false, true, false, false],
                text: '',
              },
            ],
          ],
        },
        layout: layoutConfiguration,
      },
      {
        style: 'tableData',
        color: '#444',
        table: {
          widths: [250, 'auto'],
          headerRows: 1,
          body: [
            [
              this.generateContentPdfService.createAlignmentObject(
                'Base Imponible en Moneda Extranjera USA $',
                'left',
                'header',
              ),
              this.generateContentPdfService.createAlignmentObject(
                `$ ${object.subtotal.toFixed(2)}`,
                'left',
              ),
            ],
            [
              this.generateContentPdfService.createAlignmentObject(
                `Monto IVA USA $ ${object.invoiceObject.tax}`,
                'left',
                'header',
              ),
              this.generateContentPdfService.createAlignmentObject(
                `$ ${object.taxCalculated.toFixed(2)}`,
                'left',
              ),
            ],
            [
              this.generateContentPdfService.createAlignmentObject(
                'Total Factura US$',
                'left',
                'header',
              ),
              this.generateContentPdfService.createAlignmentObject(
                `$ ${object.total.toFixed(2)}`,
                'left',
              ),
            ],
            [
              this.generateContentPdfService.createAlignmentObject(
                'Tipo de Cambio de Referencia BCV',
                'left',
                'header',
              ),
              this.generateContentPdfService.createAlignmentObject(
                `${object.rateBCV}`,
                'left',
              ),
            ],
          ],
        },
      },
      object.invoiceObject.estado === 4
        ? {
            style: 'tableData',
            color: '#444',
            table: {
              widths: ['auto'],
              body: this.generateContentPdfService.createVzlaInvoicePolicy(),
            },
            layout: layoutConfiguration,
          }
        : {
            style: 'textStyle',
            border: [false, false, false, false],
            text: '',
          },
      {
        style: 'tableData',
        color: '#444',
        table: {
          widths: [100, 'auto', '*', 100, 'auto'],
          headerRows: 2,
          body: [
            [
              this.generateContentPdfService.createAlignmentObject(
                'Chofer:',
                'left',
                'header',
              ),
              this.generateContentPdfService.createAlignmentObject(
                object.driverObject?.chofer,
                'right',
              ),
              this.generateContentPdfService.createAlignmentObject(
                '',
                'center',
              ),
              this.generateContentPdfService.createAlignmentObject(
                'Total Renglones',
                'left',
                'header',
              ),
              this.generateContentPdfService.createAlignmentObject(
                `Bs ${(object.subtotal * object.rateBCV!).toFixed(2)}`,
                'right',
              ),
            ],
            [
              this.generateContentPdfService.createAlignmentObject(
                'C.I.N°:',
                'left',
                'header',
              ),
              this.generateContentPdfService.createAlignmentObject(
                object.driverObject?.cin,
                'right',
              ),
              this.generateContentPdfService.createAlignmentObject(
                '',
                'center',
              ),
              this.generateContentPdfService.createAlignmentObject(
                '% Descuento',
                'left',
                'header',
              ),
              this.generateContentPdfService.createAlignmentObject(
                `Bs ${(object.discount * object.rateBCV!).toFixed(2)}`,
                'right',
              ),
            ],
            [
              this.generateContentPdfService.createAlignmentObject(
                'Placa:',
                'left',
                'header',
              ),
              this.generateContentPdfService.createAlignmentObject(
                object.driverObject?.placa,
                'right',
              ),
              this.generateContentPdfService.createAlignmentObject(
                '',
                'center',
              ),
              this.generateContentPdfService.createAlignmentObject(
                'Sub Total',
                'left',
                'header',
              ),
              this.generateContentPdfService.createAlignmentObject(
                `Bs ${(object.subtotal * object.rateBCV!).toFixed(2)}`,
                'right',
              ),
            ],
            [
              this.generateContentPdfService.createAlignmentObject(
                'Permiso Rasda:',
                'left',
                'header',
              ),
              this.generateContentPdfService.createAlignmentObject(
                object.driverObject?.permiso_rasda,
                'right',
              ),
              this.generateContentPdfService.createAlignmentObject(
                '',
                'center',
              ),
              this.generateContentPdfService.createAlignmentObject(
                `IVA ${object.invoiceObject.tax} %`,
                'left',
                'header',
              ),
              this.generateContentPdfService.createAlignmentObject(
                `Bs ${(object.taxCalculated * object.rateBCV!).toFixed(2)}`,
                'right',
              ),
            ],
            [
              this.generateContentPdfService.createAlignmentObject(
                'Destino:',
                'left',
                'header',
              ),
              this.generateContentPdfService.createAlignmentObject(
                object.driverObject?.destino,
                'right',
              ),
              this.generateContentPdfService.createAlignmentObject(
                '',
                'center',
              ),
              this.generateContentPdfService.createAlignmentObject(
                'Total a Pagar',
                'left',
                'header',
              ),
              this.generateContentPdfService.createAlignmentObject(
                `Bs ${(object.total * object.rateBCV!).toFixed(2)}`,
                'right',
              ),
            ],
            [
              this.generateContentPdfService.createAlignmentObject(
                'Peso Neto:',
                'left',
                'header',
              ),
              this.generateContentPdfService.createAlignmentObject(
                object.driverObject?.peso_neto,
                'right',
              ),
              this.generateContentPdfService.createAlignmentObject(
                '',
                'center',
              ),
              this.generateContentPdfService.createAlignmentObject(
                '',
                'left',
                'header',
              ),
              this.generateContentPdfService.createAlignmentObject('', 'right'),
            ],
          ],
        },
      },
      object.invoiceObject.estado === 4
        ? {
            style: 'tableData',
            color: '#444',
            table: {
              widths: ['*'],
              body: this.generateContentPdfService.generateSignature(
                object.driverObject?.son,
              ),
            },
            layout: layoutConfiguration,
          }
        : {
            style: 'textStyle',
            border: [false, false, false, false],
            text: '',
          },
    ];
    return content;
  }

  getPdfInvoiceStatus(invoiceObject: IQuota) {
    let text = '';
    switch (invoiceObject.estado) {
      case InvoiceStatus.Quotation:
        text = InvoiceStatusDescription.Quotation;
        break;
      case InvoiceStatus.Rejected:
        text = InvoiceStatusDescription.Quotation;
        break;
      case InvoiceStatus.Delivery:
        text = InvoiceStatusDescription.Delivery;
        break;
      case InvoiceStatus.Invoice:
        text = InvoiceStatusDescription.Invoice;
        break;
      default:
        text = InvoiceStatusDescription.Unknown;
        break;
    }
    return text;
  }

  getPdfInvoiceNumber(invoiceCount: IContFact, invoiceObject: IQuota) {
    let text = '';
    switch (invoiceObject.estado) {
      case InvoiceStatus.Quotation:
        text = `${invoiceCount.quotation}`;
        break;
      case InvoiceStatus.Rejected:
        text = `Rechazado`;
        break;
      case InvoiceStatus.Delivery:
        text = `${invoiceCount.delivery}`;
        break;
      case InvoiceStatus.Invoice:
        text = `${invoiceCount.invoice}`;
        break;
      default:
        text = InvoiceStatusDescription.Unknown;
        break;
    }
    return text;
  }

  generateInvoicePdfItems(
    sales: IVentaQuery[],
    rateBCV: number,
    invoiceObject: IQuota,
  ) {
    let array: any[] = [];
    sales.forEach((sale, index) => {
      const codeObject =
        this.generateContentPdfService.generateSimpleObjectNoBorder(
          sale.articulo[0].codigo!,
        );
      const descriptionObject =
        this.generateContentPdfService.generateSimpleObjectNoBorder(
          sale.articulo[0].descripcion,
        );
      const quantityObject =
        this.generateContentPdfService.generateSimpleObjectNoBorder(
          sale.cantidad.toString(),
        );

      const unitObject =
        this.generateContentPdfService.generateSimpleObjectNoBorder(
          sale.articulo[0].unit?.id_second || '',
        );
      const priceObject =
        this.generateContentPdfService.generateSimpleObjectNoBorder(
          this.converseValues(sale.precio, rateBCV, invoiceObject),
        );
      const discountObject =
        this.generateContentPdfService.generateSimpleObjectNoBorder(
          this.converseValues(sale.descuento, rateBCV, invoiceObject),
        );
      const subtotalObject =
        this.generateContentPdfService.generateSimpleObjectNoBorder(
          this.converseValues(sale.subtotal, rateBCV, invoiceObject),
        );
      array.push([
        codeObject,
        descriptionObject,
        quantityObject,
        unitObject,
        priceObject,
        discountObject,
        subtotalObject,
      ]);
    });
    const headers = this.getPdfItemHeaders();
    array.unshift(headers.header);
    const missingItemRows = this.getMissingItemRows(sales.length);
    array = array.concat(missingItemRows);
    return array;
  }

  getMissingItemRows(lenght: number) {
    let items: any[] = [];
    for (let i = 0; i < 11 - lenght; i++) {
      const emptyObject =
        this.generateContentPdfService.generateSimpleObjectNoBorder('');
      items.push([
        emptyObject,
        emptyObject,
        emptyObject,
        emptyObject,
        emptyObject,
        emptyObject,
        emptyObject,
      ]);
    }
    return items;
  }

  converseValues(value: number, rateBCV: number, invoiceObject: IQuota) {
    if (invoiceObject.estado === 2 || invoiceObject.estado === 3) {
      return `Bs ${(value * rateBCV).toFixed(2)}`;
    } else {
      return `$ ${value}`;
    }
  }

  getPdfItemHeaders() {
    return {
      header: [
        this.generateContentPdfService.createLetterheadHeaderObject('Codigo'),
        this.generateContentPdfService.createLetterheadHeaderObject(
          'Descripcion',
        ),
        this.generateContentPdfService.createLetterheadHeaderObject('Cant'),
        this.generateContentPdfService.createLetterheadHeaderObject('Unidad'),
        this.generateContentPdfService.createLetterheadHeaderObject(
          'Precio Unitario',
        ),
        this.generateContentPdfService.createLetterheadHeaderObject('% Dcto'),
        this.generateContentPdfService.createLetterheadHeaderObject('Total'),
      ],
    };
  }

  getStyles() {
    return {
      header: {
        fontSize: 8,
        bold: true,
        color: 'black',
      },
      tableData: {
        margin: [0, 5, 0, 15],
      },
      itemsTable: {
        margin: [0, 5, 0, 15],
      },
      tableHeader: {
        bold: true,
        fontSize: 8,
        color: 'black',
      },
      textStyle: {
        fontSize: 8,
        color: '#363636',
      },
    };
  }
}
